/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Grid,
  IconButton,
  Box,
  CircularProgress,
  Button
} from '@material-ui/core'
import { CloudDownload } from '@material-ui/icons'
import { Navigate } from 'react-router-dom'
import { ServerError } from '../../../schemas/Error'
import UserContext from '../../../contexts/User'
import { UserContextType } from '../../../schemas/User'
import PageContainer from '../Common/PageContainer'
import ResponsiveList, { ResponsiveColumn } from '../Common/ResponsiveList'
import SupportDocumentService from '../../../services/SupportDocument'
import Utils from '../../../utils'
import permissions from '../../../permissions.json'
import { urls } from '../../../urls'

const useStyles = makeStyles((theme: Theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  loadMoreButton: {
    minHeight: 44,
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

const texts = {
  header: {
    description: 'Lista de notas crédito de documento soporte'
  },
  body: {
    buttonLoadMore: {
      label: 'Cargar más ...'
    }
  }
}

// widthPct: porcentaje del ancho de la tabla (las columnas visibles siempre suman 100%)
const columns: ResponsiveColumn[] = [
  {
    field: 'number',
    headerName: 'Nota crédito',
    widthPct: 12,
    editable: false,
  },
  {
    field: 'date',
    headerName: 'Fecha',
    widthPct: 18,
    editable: false,
  },
  {
    field: 'third',
    headerName: 'Nombre',
    editable: false,
    widthPct: 28,
    cardTitle: true,
  },
  {
    field: 'note',
    headerName: 'Descripción',
    editable: false,
    widthPct: 22,
    hideOnTablet: true,
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    widthPct: 20,
    minWidth: 110,
    type: 'actions',
    renderCell: (params: any) => (
      <ActionButtons params={params} />
    )
  }
]

interface State {
  creditNotes: any[]
  loading: boolean
}

const initState: State = {
  creditNotes: [],
  loading: true
}

function ActionButtons(props: any) {
  const onDownloadFromDian = () => {
    if (props.params.row.qrStr !== undefined) {
      const regex = /https?:\/\/[^\s]+/g;
      const matches = props.params.row.qrStr.match(regex)
      let url = matches ? matches[0] : undefined
      return window.open(url, '_blank')
    } else {
      return toast.warn('Documento disponible ingresando a la visualización')
    }
  }

  const onDownloadPDF = async () => {
    const cude = props.params.row.cude || props.params.row.preview
    if (cude !== undefined) {
      try {
        const supportDocumentService = new SupportDocumentService()
        const htmlContent = await supportDocumentService.getCreditNoteSupportDocumentHTML(cude)

        const documentNumber = props.params.row.number
        Utils.generateCreditNoteSupportDocumentPDF(htmlContent, documentNumber)

        toast.success('PDF de nota crédito de documento soporte generado correctamente')
      } catch (error) {
        console.error('Error generating support-document credit note PDF:', error)
        toast.error(`Error generando PDF de nota crédito de documento soporte: ${error}`)
      }
    } else {
      toast.error('No hay nota crédito de documento soporte disponible')
    }
  }

  return (
    <React.Fragment>
      <IconButton
        type="button"
        onClick={() => onDownloadPDF()}
        title="Descargar nota crédito de documento soporte"
      >
        <CloudDownload fontSize={"small"} color="primary" />
      </IconButton>
      <IconButton
        type="button"
        onClick={() => onDownloadFromDian()}
        title="Descargar nota crédito de documento soporte desde la DIAN"
      >
        <CloudDownload fontSize={"small"} color="action" />
      </IconButton>
    </React.Fragment>
  )
}

/** Lists support-document credit notes from Plemsi with PDF and DIAN actions. */
export default function CreditNoteSupportDocumentList() {
  const classes = useStyles()
  const [state, setState] = useState<State>(initState)
  const [page, setPage] = useState<number>(1)
  const { userContext } = React.useContext(UserContext) as UserContextType

  const hasListPermission = userContext.permissions.some(
    permission => permission === permissions.support_document.list_credit_note
  )

  React.useEffect(() => {
    if (!hasListPermission) {
      return
    }

    async function loadData() {
      try {
        setState({
          ...state,
          loading: true
        })
        const supportDocumentService = new SupportDocumentService()
        const response = await supportDocumentService.getCreditNoteSupportDocumentsFromPlemsi(page)
        let notes: any[] = []

        if (response?.totalDocuments > 0 && Array.isArray(response?.docs)) {
          notes = response.docs.map((doc: any) => ({
            id: doc._id,
            number: doc.consecutive ?? doc.number ?? '-',
            date: `${doc.date ?? ''} ${doc.time ?? ''}`.trim() || '-',
            third: doc.seller?.name ?? '-',
            note: doc.notes ?? (doc.billing_reference?.number ? `Documento soporte ref: ${doc.billing_reference.number}` : '-'),
            preview: doc.preview,
            cude: doc.cude,
            qrStr: doc.QRStr,
            state: doc.state
          }))
        }
        setState({
          ...state,
          creditNotes: notes,
          loading: false
        })
      } catch (error) {
        setState({
          ...state,
          loading: false
        })
        if (axios.isAxiosError(error)) {
          const serverError = error as AxiosError<ServerError>
          if (serverError?.response?.data?.message) {
            return toast.error(serverError.response.data.message)
          }
        }
        toast.error('Error cargando notas crédito de documento soporte')
      }
    }
    loadData()
  }, [page, hasListPermission])

  if (!hasListPermission) {
    return <Navigate to={`/${urls.app.index}/${urls.app.main.supportDocument.list}`} replace={true} />
  }

  return (
    <PageContainer title={texts.header.description}>
      <Grid container spacing={3}>
        {/* ----- Listado: tabla (>= 600px) o cards (mobile) ------*/}
        <Grid item xs={12}>
          <ResponsiveList
            rows={state.creditNotes}
            columns={columns}
            keyId="id"
            cardActions={(row) => <ActionButtons params={{ row }} />}
          />
        </Grid>

        <Grid item xs={12} className={classes.alignRight}>
          <Button
            type="button"
            color="primary"
            className={classes.loadMoreButton}
            startIcon={<CloudDownload />}
            onClick={() => setPage(page + 1)}
          >
            {texts.body.buttonLoadMore.label}
          </Button>
        </Grid>
      </Grid>

      {state.loading && (
        <div className={classes.loadingContainer}>
          <Box>
            <CircularProgress />
          </Box>
        </div>
      )}
    </PageContainer>
  )
}
